/**
 * Speech 模型通用工具函数（对齐 image/video 的 tools.ts 形态）
 */

/**
 * 约束 value 的值在 allowedValues 中
 */
export function clampTo<T extends string | number>(
  value: T,
  allowedValues: readonly T[],
  defaultValue: T
): T {
  return allowedValues.includes(value) ? value : defaultValue;
}

/**
 * 根据路径获取对象中的值
 * 支持点号与数组下标语法，例如:
 * - 'data.items[0].url'
 * - 'response[1].result.value'
 * - 'data.items[*].url'  — wildcard: collect field from every array element, returns string[]
 */
export function getValueByPath(obj: any, path: string): any {
  const keys = path.split('.');
  let value = obj;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]!;
    if (value === null || value === undefined) {
      return undefined;
    }
    if (key.includes('[') && key.includes(']')) {
      const arrayKey = key.substring(0, key.indexOf('['));
      const indexStr = key.substring(key.indexOf('[') + 1, key.indexOf(']'));
      value = value[arrayKey];

      if (indexStr === '*') {
        if (!Array.isArray(value)) return undefined;
        const remaining = keys.slice(i + 1).join('.');
        if (!remaining) return value;
        return value
          .map((item: any) => getValueByPath(item, remaining))
          .filter((v: any) => v !== null && v !== undefined);
      }

      const index = parseInt(indexStr, 10);
      if (Array.isArray(value)) {
        value = value[index];
      } else {
        return undefined;
      }
    } else {
      value = value[key as any];
    }
  }
  return value;
}

/**
 * 判断状态值是否匹配（类型安全）
 * 支持 string[] 或 number[] 的状态集合
 */
export function isStatusMatch(
  state: any,
  statusValues?: string[] | number[]
): boolean {
  if (!statusValues || statusValues.length === 0) {
    return false;
  }
  if (typeof state === 'string' && typeof statusValues[0] === 'string') {
    return (statusValues as string[]).includes(state);
  }
  if (typeof state === 'number' && typeof statusValues[0] === 'number') {
    return (statusValues as number[]).includes(state);
  }
  return false;
}

/**
 * 从 providerOptions 中提取音频输入 URL 数组
 */
export function extractAudioUrls(providerOptions?: Record<string, any>):
  | string[]
  | undefined {
  return (providerOptions as any)?.url?.audio_input;
}

/**
 * 判断是否存在音频输入（类型守卫）
 */
export function hasAudioInput(audioUrls?: string[]): audioUrls is string[] {
  return !!(audioUrls && audioUrls.length > 0);
}
